import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useDiscoPromoTermsQuery } from '@src/queries/DiscoPromoTermsQuery.generated'
import { useDiscoPromoItemsPricingAvailabilityQuery } from '@src/queries/DiscoPromoItemsPricingAvailabilityQuery.generated'
import {
  PricingAndMaterialDetails,
  PricingAndAvailabilityModel,
} from '@src/aem-content/aem-types'
import { useDiscoPromoItemsLazyQuery } from '@src/queries/DiscoPromoItemsQuery.generated'
import Box from '@src/components/Box'
import DiscoPromoProductTable from './DiscoPromoProductTable'
import DiscoPromoContent from './DiscoPromoContent'
import DiscoPromoSearchField from './DiscoPromoSearchField'
import { makeStyles, Theme } from '@material-ui/core/styles'
import {
  DiscoPromoContentSkeletonLoader,
  DiscoPromoProductsSkeletonLoader,
} from './DiscoPromoSkeletonLoader'
import { useRouter } from '@src/routes'
import {
  MaterialsDetailFragment,
  useMaterialsDetailQuery,
} from '@src/queries/MaterialDetailsQuery.generated'
import DiscoPromoProductsResultCount from './DiscoPromoProductsResultCount'

interface DiscoPromotionProps {
  promoCodeId?: string
}

type Mode = 'idle' | 'search' | 'loadMore'

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    marginTop: theme.spacing(8),
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(10),
    },
  },
}))

const DISCO_PROMO_LIST_PAGE_SIZE = 20

const DiscoPromotion: FC<DiscoPromotionProps> = ({ promoCodeId }) => {
  const classes = useStyles()
  const router = useRouter()
  const [page, setPage] = useState<number>(0)
  const [mode, setMode] = useState<Mode>('idle')
  const [showLoadMoreBtn, setShowLoadMoreBtn] = useState<boolean>(true)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [discoPromoProducts, setDiscoPromoProducts] = useState<
    PricingAndMaterialDetails[]
  >([])
  const [discoPromoProductsMasterList, setDiscoPromoProductsMasterList] =
    useState<PricingAndMaterialDetails[]>([])

  /** Promo Terms Query */
  const {
    data: promoTermsData,
    loading: promoTermsDataLoading,
    error: promoTermsDataError,
  } = useDiscoPromoTermsQuery({
    variables: { input: { uuid: (promoCodeId as string) ?? '' } },
    skip: !promoCodeId,
    fetchPolicy: 'no-cache',
  })

  const {
    discount = 0,
    qualifyingAmount = 0,
    currency = '',
    validTo = '',
    message: promoTermsErrorMessage,
  } = promoTermsData?.getPromoTerms ?? {}

  /** Promo Items Query */
  const [
    getPromoItems,
    {
      data: promoItemsData,
      loading: promoItemsDataLoading,
      error: promoItemsDataError,
    },
  ] = useDiscoPromoItemsLazyQuery({ fetchPolicy: 'no-cache' })

  useEffect(() => {
    if (promoCodeId)
      getPromoItems({
        variables: {
          input: {
            uuid: (promoCodeId as string) ?? '',
            searchTerm,
            page,
            size: DISCO_PROMO_LIST_PAGE_SIZE,
          },
        },
      })
  }, [promoCodeId, page, getPromoItems])

  const {
    items,
    totalPages,
    message: promoItemsErrorMessage,
  } = promoItemsData?.getPromoItems || {}

  const productDetails = useMemo(() => {
    return (
      items
        ?.map((item) => ({
          product: item?.itemIdentifier ?? '',
          quantity: 1,
        }))
        ?.filter((sku) => sku.product) ?? []
    )
  }, [items])

  /** Pricing & Availability Query */
  const {
    data: promoItemsPricingAvailabilityData,
    loading: promoItemsPricingAvailabilityDataLoading,
    error: promoItemsPricingAvailabilityDataError,
  } = useDiscoPromoItemsPricingAvailabilityQuery({
    variables: { productDetails },
    skip: !productDetails.length,
    fetchPolicy: 'no-cache',
  })

  /** Materials Details Query */
  const {
    data: promoItemsDetailsData,
    loading: promoItemsDetailsDataLoading,
    error: promoItemsDetailsDataError,
  } = useMaterialsDetailQuery({
    variables: {
      materialNumbers:
        items
          ?.map((m) => m?.itemIdentifier)
          .filter((id): id is string => Boolean(id)) ?? [],
    },
    fetchPolicy: 'no-cache',
    skip: !items?.length,
  })

  /**
   * Merge pricing and material data by matching product <-> material_number
   * @param {Array} pricingData - Array of pricingAndAvailabilityModels
   * @param {Array} materialData - Array of material objects
   * @returns {Array} merged array with flattened fields
   */
  const mergePricingAndMaterial = (
    pricingData: PricingAndAvailabilityModel[],
    materialData: MaterialsDetailFragment[]
  ) => {
    const materialMap = new Map<string, MaterialsDetailFragment>(
      materialData.map((m) => [m.number, m])
    )

    return pricingData.map((pricing) => {
      const material = materialMap.get(pricing.product)

      return {
        productsku: pricing.product,
        product: material?.product,
        materialNumber: material?.number,
        productName: material?.name,
        productDescription: material?.description,
        isMarketplace: material?.isMarketplace,
        brand: material?.brand,
        items: pricing.items,
        alternateMaterials: pricing.alternateMaterials,
        errorMsg: pricing.errorMsg,
        dchainMessage: pricing.dchainMessage,
        hideAddToCart: pricing.hideAddToCart,
        productInfo: pricing.productInfo,
      }
    })
  }

  const appendToProductLists = useCallback(
    (prev: PricingAndMaterialDetails[], next: PricingAndMaterialDetails[]) => {
      const updated = next.map((np) => {
        const existing = prev.find((p) => p.product === np.product)
        return existing ? { ...existing, ...np } : np
      })
      const preserved = prev.filter(
        (p) => !next.some((np) => np.product === p.product)
      )
      return [...preserved, ...updated]
    },
    []
  )

  /** Update Product List on initial page load, load more and search */
  useEffect(() => {
    const pricingModels = (
      promoItemsPricingAvailabilityData?.getPromoItemsPricingAvailability
        ?.pricingAndAvailabilityModels ?? []
    ).filter(
      (model): model is PricingAndAvailabilityModel =>
        model !== null && !!model.product && !!model.items?.length
    )

    const materialData: MaterialsDetailFragment[] = (
      promoItemsDetailsData?.getMaterialsDetail ?? []
    ).filter((m): m is MaterialsDetailFragment => m !== null)

    // Wait until both arrays have data
    if (pricingModels.length === 0 || materialData.length === 0) return

    const mergedPricingAndMaterialDetails = mergePricingAndMaterial(
      pricingModels,
      materialData
    )

    if (mergedPricingAndMaterialDetails.length > 0) {
      if (mode === 'search')
        // Replace list for searching
        setDiscoPromoProducts(mergedPricingAndMaterialDetails)
      else if (mode === 'loadMore') {
        // Append list and avoid duplicates
        setDiscoPromoProducts((prev) =>
          appendToProductLists(prev, mergedPricingAndMaterialDetails)
        )
        setDiscoPromoProductsMasterList((prev) =>
          appendToProductLists(prev, mergedPricingAndMaterialDetails)
        )
      } else {
        // Fresh data → reset both lists
        setDiscoPromoProducts(mergedPricingAndMaterialDetails)
        setDiscoPromoProductsMasterList(mergedPricingAndMaterialDetails)
      }
    }
  }, [
    promoItemsPricingAvailabilityData,
    promoItemsDetailsData,
    mode,
    searchTerm,
  ])

  /** handle disco item search */
  const handleDiscoItemSearch = useCallback(
    (searchTerm: string) => {
      if (promoCodeId) {
        getPromoItems({
          variables: {
            input: {
              uuid: promoCodeId ?? '',
              searchTerm,
              page: 0,
              size: DISCO_PROMO_LIST_PAGE_SIZE,
            },
          },
        })
        setSearchTerm(searchTerm)
        setMode('search')
        setShowLoadMoreBtn(false)
      }
      if (searchTerm === '') {
        setShowLoadMoreBtn(true)
        setMode('idle')
      }
      if (searchTerm !== '') setDiscoPromoProducts([])
    },
    [promoCodeId, getPromoItems]
  )

  /** Search function for product name and description */
  const searchProducts = (
    products: PricingAndMaterialDetails[],
    searchQuery: string
  ): PricingAndMaterialDetails[] => {
    const lowerSearchQuery = searchQuery.trim().toLowerCase()
    if (!lowerSearchQuery) return products

    return products.filter(
      (item) =>
        item.product?.toLowerCase() === lowerSearchQuery ||
        item.productName?.toLowerCase().includes(lowerSearchQuery) ||
        item.productDescription?.toLowerCase().includes(lowerSearchQuery)
    )
  }

  /** Handle FE search results for Product name and Description */
  useEffect(() => {
    if (mode === 'search' && (!items || items.length === 0)) {
      const searchResult = searchProducts(
        discoPromoProductsMasterList,
        searchTerm
      )
      setDiscoPromoProducts(searchResult)
    }
  }, [items])

  const loadMoreItems = () => {
    setMode('loadMore')
    setPage((prev) => prev + 1)
  }

  /** Handle Load more button visibility */
  useEffect(() => {
    if (
      totalPages &&
      (totalPages <= 1 ||
        (promoItemsPricingAvailabilityData && page + 1 >= totalPages))
    )
      setShowLoadMoreBtn(false)
  }, [items, promoItemsPricingAvailabilityData])

  /** pagination values */
  const itemCount = totalPages
    ? totalPages *
      (mode === 'search' && items ? items?.length : DISCO_PROMO_LIST_PAGE_SIZE)
    : discoPromoProducts.length
  const pageEnd = discoPromoProducts.length
  const pageStart = discoPromoProducts.length ? 1 : null

  const hasError = [
    promoTermsDataError,
    promoItemsDataError,
    promoItemsPricingAvailabilityDataError,
    promoItemsDetailsDataError,
    promoItemsErrorMessage,
    promoTermsErrorMessage,
  ].some(Boolean)

  const isPricingDataLoading =
    promoItemsDataLoading ||
    promoItemsPricingAvailabilityDataLoading ||
    promoItemsDetailsDataLoading

  /** redirect to '/promotions' if any of api fails */
  useEffect(() => {
    if ((hasError && mode === 'idle') || !promoCodeId)
      router.push('/promotions')
  }, [promoCodeId, hasError, mode, router])

  /** Products section */
  const renderProductSection = () => {
    if (mode !== 'loadMore' && isPricingDataLoading)
      return <DiscoPromoProductsSkeletonLoader />

    if (discoPromoProducts.length > 0) {
      return (
        <>
          {itemCount && pageEnd && pageStart && (
            <DiscoPromoProductsResultCount
              itemCount={itemCount}
              pageStart={pageStart}
              pageEnd={pageEnd}
              isSearchResult={mode === 'search'}
            />
          )}
          <DiscoPromoProductTable
            discoPromoProducts={discoPromoProducts}
            loadMoreItems={loadMoreItems}
            pricingDataLoading={isPricingDataLoading}
            showLoadMoreBtn={showLoadMoreBtn}
          />
        </>
      )
    }

    return null
  }

  if (promoTermsDataLoading) return <DiscoPromoContentSkeletonLoader />

  return (
    <>
      {!hasError && promoTermsData ? (
        <Box className={classes.container}>
          <DiscoPromoContent
            {...{ discount, qualifyingAmount, currency, validTo }}
          />

          <DiscoPromoSearchField
            handleDiscoItemSearch={handleDiscoItemSearch}
            searchTerm={searchTerm}
          />

          {renderProductSection()}

          <DiscoPromoContent
            {...{ discount, qualifyingAmount, currency, validTo }}
            disclaimer
          />
        </Box>
      ) : (
        <DiscoPromoContentSkeletonLoader />
      )}
    </>
  )
}

export default DiscoPromotion
